import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Target, Cpu, Users, Briefcase } from 'lucide-react';

const Home = () => {
  const { t, language } = useLanguage();

  const offerings = [
    {
      icon: Target,
      title: t.offerings.sourcing.title,
      description: t.offerings.sourcing.description,
      link: '/expertise#sourcing'
    },
    {
      icon: Cpu,
      title: t.offerings.sap.title,
      description: t.offerings.sap.description,
      link: '/expertise#sap'
    },
    {
      icon: Users,
      title: t.offerings.change.title,
      description: t.offerings.change.description,
      link: '/expertise#change'
    },
    {
      icon: Briefcase,
      title: t.offerings.transition.title,
      description: t.offerings.transition.description,
      link: '/expertise#transition'
    }
  ];

  const stats = [
    { value: '10+', label: language === 'fr' ? 'Pays' : 'Countries' },
    { value: '30+', label: language === 'fr' ? 'Projets réalisés' : 'Completed projects' },
    { value: '15+', label: language === 'fr' ? 'Opérations de sourcing' : 'Sourcing operations' },
    { value: '+1B€', label: language === 'fr' ? 'Contrats outsourcés' : 'Outsourced contracts' }
  ];

  const heroContent = {
    subtitle: language === 'fr' 
      ? "Votre partenaire de confiance pour accélérer votre transformation digitale et repenser vos modèles de sourcing."
      : "Your trusted partner to accelerate your digital transformation and rethink your sourcing models.",
    description: language === 'fr'
      ? "Walaby est un cabinet de conseil indépendant qui accompagne les organisations dans leurs transformations digitales, organisationnelles et humaines. Nous aidons les dirigeants à concevoir des modèles opérationnels performants, à structurer des stratégies de sourcing durables et à sécuriser l'exécution de leurs programmes de transformation."
      : "Walaby is an independent consulting firm that supports organizations in their digital, organizational and human transformations. We help leaders design high-performing operating models, structure sustainable sourcing strategies and secure the execution of their transformation programs.",
    industry: language === 'fr'
      ? "Notre expertise s'est développée au cœur de l'industrie pharmaceutique et s'étend aujourd'hui à d'autres secteurs confrontés à des enjeux de transformation technologique et organisationnelle majeurs."
      : "Our expertise has developed at the heart of the pharmaceutical industry and now extends to other sectors facing major technological and organizational transformation challenges."
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-slate-50" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-100 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-6 block">
                {t.hero.tagline}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6">
                {t.hero.title}{' '}
                <span className="text-[#E5A81D]">{t.hero.titleHighlight}</span>
              </h1>
              <p className="font-heading text-xl text-[#E5A81D] font-medium mb-4">
                {heroContent.subtitle}
              </p>
              <p className="font-body text-base text-slate-600 leading-relaxed mb-4 max-w-xl">
                {heroContent.description}
              </p>
              <p className="font-body text-sm text-slate-500 leading-relaxed mb-10 max-w-xl">
                {heroContent.industry}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center bg-[#0F172A] text-white px-8 py-4 font-heading text-sm font-medium tracking-wide hover:bg-[#1E293B] transition-all duration-300"
                >
                  {t.hero.cta}
                  <ArrowRight size={18} className="ml-2" />
                </Link>
                <Link
                  to="/expertise"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center border border-[#0F172A]/20 text-[#0F172A] px-8 py-4 font-heading text-sm font-medium tracking-wide hover:bg-[#0F172A]/5 transition-all duration-300"
                >
                  {t.hero.ctaSecondary}
                </Link>
              </div>
            </div>
            
            <div className="hidden lg:block animate-fade-in animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E5A81D]/10 -z-10" />
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                  alt="Corporate transformation"
                  className="w-full h-[500px] object-cover shadow-hover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0F172A] py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="font-heading text-4xl md:text-5xl font-bold text-[#E5A81D] mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-sm text-slate-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offerings Preview */}
      <section className="py-24 md:py-32 bg-white" data-testid="offerings-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block">
              {t.offerings.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A]">
              {t.offerings.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {offerings.map((offering, index) => (
              <Link
                key={index}
                to={offering.link}
                className="card-offering group relative overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`offering-card-${index}`}
              >
                <div className="absolute top-0 left-0 w-1 h-0 bg-[#E5A81D] group-hover:h-full transition-all duration-500" />
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-slate-50 group-hover:bg-[#E5A81D]/10 transition-colors">
                    <offering.icon size={24} className="text-[#E5A81D]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2 group-hover:text-[#E5A81D] transition-colors">
                      {offering.title}
                    </h3>
                    <p className="font-body text-slate-600 text-sm leading-relaxed">
                      {offering.description}
                    </p>
                  </div>
                  <ArrowRight size={20} className="text-slate-300 group-hover:text-[#E5A81D] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/expertise"
              className="inline-flex items-center font-heading text-sm font-medium text-[#0F172A] hover:text-[#E5A81D] transition-colors"
              data-testid="view-all-expertise"
            >
              {t.hero.ctaSecondary}
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-50" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-6">
            {t.contact.subtitle}
          </h2>
          <p className="font-body text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {t.contact.description}
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact"
            className="inline-flex items-center justify-center bg-[#E5A81D] text-[#0F172A] px-8 py-4 font-heading text-sm font-bold hover:bg-[#E5A81D]/90 hover:shadow-lg transition-all duration-300"
          >
            {t.hero.cta}
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
